// chakra imports
import {
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { InstallmentCalculatorModal } from 'components/InstallmentCalculatorModal';
import Links from 'components/sidebar/components/Links';
import SidebarItem from 'components/sidebar/components/SidebarItem';
import { usePrivacy } from 'contexts/PrivacyContext';
import useAuth from 'contexts/useAuth';
import { FaSignOutAlt, FaWhatsapp } from 'react-icons/fa';
import { maskCurrency } from 'utils/number';
import { getVariable } from 'whitelabel';
import { maskAccountNumber } from 'utils/account';
import { MdCopyAll, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import AccountSwitcher from './AccountSwitcher';
import FeedbackButton from 'components/feedback/FeedbackButton';

// FUNCTIONS

function SidebarContent(props: { routes: RoutesType[]; closeSidebar: () => void }) {
  const { routes } = props;
  const { userData, signOut } = useAuth();
  const toast = useToast();
  const { valuesHidden, toggleValues } = usePrivacy();
  let textColor = useColorModeValue('gray.200', 'gray.200');

  const AccountInfo = () => {
    return (
      <>
        <Text
          w="100%"
          fontSize="sm"
          fontWeight="500"
          color={textColor}
          opacity={0.8}
          noOfLines={1}
        >
          Olá, {userData?.name}
        </Text>
        <HStack
          w="100%"
          spacing={1}
          alignItems="center"
        >
          <Text
            fontSize="xs"
            color={textColor}
            opacity={0.8}
          >
            Conta {userData?.accountNumber ? maskAccountNumber(userData?.accountNumber) : 'não informada'}
          </Text>
          {userData?.accountNumber ? (
            <Icon
              as={MdCopyAll}
              aria-label="Copiar número da conta"
              color="whiteAlpha.800"
              w="16px"
              h="16px"
              cursor="pointer"
              _hover={{ color: 'white' }}
              onClick={() => {
                navigator.clipboard.writeText(userData?.accountNumber || '');
                toast({
                  title: 'Número da conta copiado',
                  status: 'success',
                  duration: 2000,
                  position: 'top',
                  isClosable: true,
                });
              }}
            />
          ) : null}
        </HStack>
      </>
    );
  };
  // SIDEBAR
  return (
    <Flex
      direction="column"
      height="100%"
      pt="25px"
      borderRadius="30px"
    >
      <Flex
        px={6}
        pb={6}
        justifyContent="flex-start"
      >
        <Image
          alt={getVariable('name')}
          src={getVariable('logo_inverted')}
          maxWidth={180}
        />
      </Flex>

      <Stack
        direction="column"
        mt="8px"
        mb="auto"
        pb="20"
      >
        <VStack
          alignItems="flex-start"
          pl={8}
          pr={4}
          mb={6}
          spacing={1}
        >
          <AccountInfo />
          {/* Os saldos só aparecem aqui no mobile: acima de `md` eles vivem no header. */}
          <Box
            display={{ base: 'block', md: 'none' }}
            w="100%"
          >
            <Divider my={3} />
            <HStack
              justifyContent="space-between"
              alignItems="center"
              w="100%"
            >
              <Text
                color="gray.200"
                fontSize="xs"
                opacity={0.8}
              >
                Saldo disponível
              </Text>
              <IconButton
                aria-label={valuesHidden ? 'Mostrar valores' : 'Ocultar valores'}
                icon={<Icon as={valuesHidden ? MdVisibilityOff : MdVisibility} w="18px" h="18px" />}
                onClick={toggleValues}
                variant="ghost"
                size="sm"
                color="whiteAlpha.800"
                _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
              />
            </HStack>
            <Text
              color={!valuesHidden && Number(userData?.balance ?? 0) < 0 ? 'red.300' : 'white'}
              fontSize="lg"
              fontWeight="bold"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {maskCurrency(userData.balance)}
            </Text>
            <Text
              color="gray.200"
              fontSize="sm"
              opacity={0.9}
            >
              Saldo pendente: {maskCurrency(userData.balancePending)}
            </Text>
            <Text
              color="gray.200"
              fontSize="sm"
              opacity={0.9}
            >
              Saldo bloqueado: {maskCurrency(userData.amountLocked)}
            </Text>
          </Box>
        </VStack>

        <AccountSwitcher />

        <HStack
          mt={2}
          px="16px"
          w="full"
          borderRadius="12px"
          cursor="pointer"
          pr="34px"
        >
          <InstallmentCalculatorModal />
        </HStack>

        <Box
          ps="20px"
          pe={{ lg: '16px', '2xl': '16px' }}
        >
          <Links
            routes={routes}
            closeSidebar={props.closeSidebar}
          />
          <Box mt={2}>
            <SidebarItem
              icon={FaWhatsapp}
              label="Suporte WhatsApp"
              onClick={() => window.open(getVariable('supportWhatsapp'), '_blank')}
            />
            <SidebarItem
              icon={FaSignOutAlt}
              label="Sair"
              onClick={() => signOut(null)}
            />
            <Box mt="14px">
              <FeedbackButton />
            </Box>
          </Box>
        </Box>
      </Stack>
    </Flex>
  );
}

export default SidebarContent;
